class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.closeBtn = document.getElementById('cart-drawer-close');
    this.itemsContainer = document.getElementById('cart-drawer-items');
    this.emptyState = document.getElementById('cart-drawer-empty');
    this.subtotalEl = document.getElementById('cart-drawer-subtotal');

    // Bind global header cart icon if it exists, excluding the checkout/view-cart buttons inside the drawer itself
    this.headerCartIcons = document.querySelectorAll('.cart-icon-btn, a[href="/cart"]:not(#cart-drawer *)');
    this.changeDebouncers = {}; // Map of itemKey -> timeoutId

    this.init();
  }

  init() {
    if (!this.drawer) return;

    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    this.headerCartIcons.forEach(icon => {
      icon.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Fetch initial state silently on page load
    this.fetchCart();
  }

  // ── Open drawer — shows cached state instantly, only fetches if cold start ──
  open() {
    // Show drawer immediately with whatever we have
    this._showDrawer();
    window.NMCLI.trapFocus(this.drawer, 'cart-drawer');

    // Only hit the network if we have no cached state yet
    if (!this.currentCart) {
      this.fetchCart();
    }
  }

  _showDrawer() {
    this.drawer.classList.remove('translate-x-full');
    this.overlay.classList.remove('opacity-0', 'pointer-events-none');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.add('translate-x-full');
    this.overlay.classList.add('opacity-0', 'pointer-events-none');
    document.body.style.overflow = '';
    window.NMCLI.removeTrapFocus(this.drawer, 'cart-drawer');
  }

  formatMoney(cents) {
    return window.NMCLI.formatMoney(cents);
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.currentCart = cart;
      this.renderCart(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  renderCart(cart) {
    if (!this.drawer || !this.itemsContainer || !this.emptyState || !this.subtotalEl) return;
    if (!cart || !Array.isArray(cart.items)) return; // Guard against malformed responses

    // Update global cart badge
    const badge = document.getElementById('header-cart-count');
    if (badge) {
      badge.textContent = cart.item_count;
      if (cart.item_count === 0) {
        badge.classList.remove('scale-100');
        badge.classList.add('scale-0');
      } else {
        badge.classList.remove('scale-0');
        badge.classList.add('scale-100');
      }
    }

    if (cart.item_count === 0) {
      this.emptyState.classList.remove('hidden');
      this.itemsContainer.innerHTML = '';
      this.subtotalEl.innerText = this.formatMoney(0);

      // Hide meters on empty cart
      document.getElementById('cart-drawer-shipping-meter')?.classList.add('hidden');
      document.getElementById('cart-drawer-gift-meter')?.classList.add('hidden');
      document.getElementById('cart-drawer-gift-wrap-wrapper')?.classList.add('opacity-50', 'pointer-events-none');

      // Fetch empty cart upsells
      this.fetchEmptyCartRecommendations();
      return;
    }

    this.emptyState.classList.add('hidden');
    this.subtotalEl.innerText = this.formatMoney(cart.total_price);
    this.subtotalEl.classList.remove('opacity-60');

    // Pre-calculate if we have a gift that is about to be auto-removed
    const giftMeter = document.getElementById('cart-drawer-gift-meter');
    const giftThresholdAttr = giftMeter ? giftMeter.getAttribute('data-gift-threshold') : null;
    const giftVariantIdsAttr = giftMeter ? giftMeter.getAttribute('data-gift-variant-ids') : null;
    
    let freeGiftItemKeyToRemove = null;
    if (giftThresholdAttr && giftVariantIdsAttr) {
      const giftThresholdCents = parseInt(giftThresholdAttr) * 100;
      const giftVariantIds = giftVariantIdsAttr.split(',').filter(id => id).map(id => parseInt(id));
      const freeGiftItem = cart.items.find(i => giftVariantIds.includes(i.variant_id));
      if (freeGiftItem) {
        let qualifyingTotal = cart.total_price - freeGiftItem.final_line_price;
        if (qualifyingTotal < giftThresholdCents) {
          freeGiftItemKeyToRemove = freeGiftItem.key;
        }
      }
    }

    this.itemsContainer.innerHTML = cart.items.filter(item => {
      // Hide the gift visually if it's queued for automatic removal
      if (item.key === freeGiftItemKeyToRemove) return false;
      return true;
    }).map(item => {
      const safeImage = item.image ? window.NMCLI.escapeHtml(item.image) : '';
      const safeKey = window.NMCLI.escapeHtml(item.key);
      const safeTitle = window.NMCLI.escapeHtml(item.product_title);
      const safeVariant = item.variant_title ? window.NMCLI.escapeHtml(item.variant_title) : '';
      return `
      <div class="flex gap-4 border-b border-black/5 pb-6">
        <div class="w-24 h-32 bg-surface-container rounded overflow-hidden flex-shrink-0">
          ${safeImage ? `<img src="${safeImage}" alt="${safeTitle}" class="w-full h-full object-cover">` : ''}
        </div>
        <div class="flex flex-col flex-1 justify-between py-1">
          <div>
            <h3 class="font-body-md text-northern-navy text-sm font-bold">${safeTitle}</h3>
            ${safeVariant && safeVariant !== 'Default Title' ? `<p class="font-body-md text-northern-navy/60 text-xs mt-1">${safeVariant}</p>` : ''}
            <div class="font-body-md text-northern-navy text-sm mt-2">${this.formatMoney(item.final_line_price)}</div>
          </div>
          <div class="flex items-center justify-between mt-4">
            <div class="flex items-center border border-northern-navy/20 rounded">
              <button data-cart-key="${safeKey}" data-new-qty="${item.quantity - 1}" class="cart-drawer-qty-btn w-8 h-8 flex items-center justify-center text-northern-navy hover:bg-surface-bright transition-colors font-bold text-xs">-</button>
              <span class="w-8 text-center font-body-md text-sm text-northern-navy font-bold">${item.quantity}</span>
              <button data-cart-key="${safeKey}" data-new-qty="${item.quantity + 1}" class="cart-drawer-qty-btn w-8 h-8 flex items-center justify-center text-northern-navy hover:bg-surface-bright transition-colors font-bold text-xs">+</button>
            </div>
            <button data-cart-key="${safeKey}" data-new-qty="0" class="cart-drawer-qty-btn text-xs font-nav-link uppercase tracking-widest text-northern-navy/50 hover:text-red-500 transition-colors underline">Remove</button>
          </div>
        </div>
      </div>
    `}).join('');

    // Bind cart drawer quantity buttons via event delegation (avoids inline onclick XSS)
    this.itemsContainer.querySelectorAll('.cart-drawer-qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-cart-key');
        const qty = parseInt(btn.getAttribute('data-new-qty'), 10);
        if (key) window.CartDrawer.changeItem(key, qty);
      });
    });

    // Update Shipping Meter
    const meter = document.getElementById('cart-drawer-shipping-meter');
    const thresholdAttr = meter ? meter.getAttribute('data-threshold') : null;
    if (meter && thresholdAttr) {
      meter.classList.remove('hidden');
      const thresholdCents = parseInt(thresholdAttr) * 100;
      const progress = Math.min((cart.total_price / thresholdCents) * 100, 100);
      const text = document.getElementById('cart-drawer-shipping-text');
      const bar = document.getElementById('cart-drawer-shipping-bar');

      if (bar) bar.style.width = `${progress}%`;
      if (text) {
        if (progress >= 100) {
          text.innerHTML = '✨ You have unlocked <span class="text-antique-gold">FREE SHIPPING</span>';
        } else {
          const diff = thresholdCents - cart.total_price;
          text.innerHTML = `You are <span class="text-antique-gold">${this.formatMoney(diff)}</span> away from FREE SHIPPING`;
        }
      }
    }

    // Update Free Gift Meter and Carousel (skip during optimistic renders — server truth needed)
    let giftProgress = 100; // Default to eligible if no threshold

    if (giftMeter && giftThresholdAttr && giftVariantIdsAttr && !cart._optimistic) {
      giftMeter.classList.remove('hidden');
      const giftThresholdCents = parseInt(giftThresholdAttr) * 100;
      const giftVariantIds = giftVariantIdsAttr.split(',').filter(id => id).map(id => parseInt(id));

      // We check for presence of the gift.
      const freeGiftItem = cart.items.find(i => giftVariantIds.includes(i.variant_id));
      const hasGift = !!freeGiftItem;

      // Calculate the subtotal EXCLUDING the free gift, because if they drop below $200,
      // Shopify reinstates the gift's price which falsely inflates the cart total over $200!
      let qualifyingTotal = cart.total_price;
      if (hasGift) {
        qualifyingTotal -= freeGiftItem.final_line_price;
      }

      // Eligibility is based strictly on the qualifying items
      giftProgress = Math.min((qualifyingTotal / giftThresholdCents) * 100, 100);

      const text = document.getElementById('cart-drawer-gift-text');
      const bar = document.getElementById('cart-drawer-gift-bar');
      const selector = document.getElementById('cart-drawer-gift-selector');

      if (bar) bar.style.width = `${giftProgress}%`;
      if (text) {
        if (giftProgress >= 100) {
          if (hasGift) {
            text.innerHTML = `✨ You selected a <span class="text-[#C8A46A]">FREE GIFT</span>`;
            if (selector) selector.classList.add('hidden'); // Hide carousel if they made a choice
          } else {
            text.innerHTML = '✨ You unlocked a <span class="text-[#C8A46A]">FREE GIFT</span>! Pick one below:';
            if (selector) selector.classList.remove('hidden'); // Show carousel
          }
        } else {
          const diff = giftThresholdCents - qualifyingTotal;
          text.innerHTML = `You are <span class="text-[#C8A46A]">${this.formatMoney(diff)}</span> away from a FREE GIFT`;
          if (selector) selector.classList.add('hidden'); // Hide carousel
          // Note: gift auto-removal is handled in changeItem() via batched /cart/update.js
        }
      }
    }

    // Update Gift Wrap Checkbox state and Eligibility
    const giftWrapCb = document.getElementById('cart-drawer-gift-wrap');
    const giftWrapWrapper = document.getElementById('cart-drawer-gift-wrap-wrapper');
    if (giftWrapCb) {
      const gwId = parseInt(giftWrapCb.getAttribute('data-variant-id'));
      const hasGiftWrap = cart.items.some(item => item.variant_id === gwId);
      giftWrapCb.checked = hasGiftWrap;

      if (giftWrapWrapper) {
        if (giftProgress < 100) {
          giftWrapWrapper.classList.add('opacity-50', 'pointer-events-none');
        } else {
          giftWrapWrapper.classList.remove('opacity-50', 'pointer-events-none');
        }
      }

      // Smart Auto-Removal: If Gift Wrap is the ONLY item left in the cart, remove it
      if (hasGiftWrap && cart.items.length === 1 && !this._isTogglingGiftWrap) {
        this._isTogglingGiftWrap = true;
        const gwItem = cart.items.find(item => item.variant_id === gwId);
        if (gwItem) {
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: gwItem.key, quantity: 0 })
          }).then(() => {
            this._isTogglingGiftWrap = false;
            this.fetchCart();
          });
        }
      }
    }

    // Fetch recommendations based on the first item in the cart, or use empty cart upsells
    if (cart.items.length > 0) {
      this.fetchRecommendations(cart.items[0].product_id);
    } else {
      this.fetchEmptyCartRecommendations();
    }

    // Fire global event so the cart page knows to sync (skip during optimistic renders)
    if (!cart._optimistic) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    }
  }

  async fetchEmptyCartRecommendations() {
    try {
      const response = await fetch('/collections/all/products.json?limit=4');
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      if (data.products && data.products.length > 0) {
        this.renderRecommendations(data.products);
      } else {
        document.getElementById('cart-drawer-recommendations')?.classList.add('hidden');
      }
    } catch (e) {
      console.error('Failed to load empty cart recommendations', e);
      document.getElementById('cart-drawer-recommendations')?.classList.add('hidden');
    }
  }

  async fetchRecommendations(productId) {
    if (!productId) return;
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=4`);
      const data = await response.json();
      this.renderRecommendations(data.products);
    } catch (e) {
      console.error('Failed to load cart recommendations', e);
    }
  }

  renderRecommendations(products) {
    const container = document.getElementById('cart-drawer-recommendations');
    const list = document.getElementById('cart-drawer-recommendations-list');
    if (!container || !list) return;

    if (!products || products.length === 0) {
      container.classList.add('hidden');
      return;
    }

    container.classList.remove('hidden');
    container.classList.add('flex');

    list.innerHTML = products.map(product => {
      const firstVariant = product.variants ? product.variants[0] : null;
      const variantId = firstVariant ? firstVariant.id : product.id;
      let image = (product.images && product.images.length > 0) ? product.images[0]
                   : product.featured_image || product.image || '';
      if (typeof image === 'object' && image !== null) image = image.src || image.url || '';
      const safeImage = window.NMCLI.escapeHtml(String(image));
      const safeUrl = window.NMCLI.escapeHtml(product.url || '');
      const safeTitle = window.NMCLI.escapeHtml(product.title || '');
      const price = typeof product.price === 'number' ? product.price
                   : (parseFloat(product.price) * 100) || 0;
      return `
        <div class="flex-shrink-0 w-32 flex flex-col gap-2">
          <a href="${safeUrl}" class="block w-full aspect-[3/4] bg-surface-container rounded overflow-hidden">
            ${safeImage ? `<img src="${safeImage}" class="w-full h-full object-cover">` : ''}
          </a>
          <a href="${safeUrl}" class="font-sans text-[11px] font-semibold text-northern-navy hover:text-[#C8A46A] transition-colors leading-tight truncate block w-full">
            ${safeTitle}
          </a>
          <div class="flex items-center justify-between">
            <span class="font-sans text-xs font-bold text-northern-navy">${this.formatMoney(price)}</span>
            <button type="button" data-add-variant-id="${parseInt(variantId, 10)}" class="cart-drawer-add-btn text-xs font-bold bg-northern-navy text-pearl-ivory px-2 py-1 rounded hover:bg-antique-gold transition-colors">Add</button>
          </div>
        </div>
      `;
    }).join('');

    // Bind add buttons via event delegation (avoids inline onclick)
    list.querySelectorAll('.cart-drawer-add-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const vid = parseInt(btn.getAttribute('data-add-variant-id'), 10);
        if (vid) window.CartDrawer.addItem(vid);
      });
    });
  }

  async swapGift(newVariantId, allVariantIdsStr) {
    if (this._isTogglingGift) return;
    this._isTogglingGift = true;

    try {
      if (!this.currentCart) await this.fetchCart();
      
      // Parse all possible gift variant IDs
      const allVariantIds = allVariantIdsStr.split(',').filter(id => id).map(id => parseInt(id));

      // Only remove items that are ACTUALLY free gifts based on variant ID
      const existingFreeGifts = this.currentCart.items.filter(
        i => allVariantIds.includes(i.variant_id)
      );

      // Remove existing free gifts in a single batched request to avoid race conditions
      if (existingFreeGifts.length > 0) {
        let updates = {};
        for (const item of existingFreeGifts) updates[item.key] = 0;
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
      }

      // Add the newly selected gift
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: newVariantId, quantity: 1 })
      });

      // Always re-fetch the full cart from server to get accurate state
      await this.fetchCart();
    } catch (error) {
      console.error('Error swapping gift:', error);
      await this.fetchCart();
    } finally {
      this._isTogglingGift = false;
    }
  }

  // ── Show a temporary "Adding..." row while the request is in-flight ──
  _showAddingIndicator(variantId) {
    if (!this.itemsContainer) return;
    const existingIndicator = this.itemsContainer.querySelector('#cart-adding-row');
    if (existingIndicator) return; // Don't double-add

    const row = document.createElement('div');
    row.id = 'cart-adding-row';
    row.className = 'flex gap-4 border-b border-black/5 pb-6 opacity-50 animate-pulse';
    row.innerHTML = `
      <div class="w-24 h-32 bg-surface-container rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
        <span class="material-symbols-outlined text-antique-gold text-2xl animate-spin">sync</span>
      </div>
      <div class="flex flex-col flex-1 justify-center py-1">
        <div class="h-3 bg-surface-container rounded w-3/4 mb-2"></div>
        <div class="h-3 bg-surface-container rounded w-1/2"></div>
      </div>
    `;
    // Prepend so it's visible at the top
    this.itemsContainer.prepend(row);
  }

  _removeAddingIndicator() {
    document.getElementById('cart-adding-row')?.remove();
  }

  // ── Fast add: one network request, optimistic in-memory update, no refetch ──
  async addItem(variantId, quantity = 1) {
    // 1. Open drawer instantly with current cached state
    this._showDrawer();

    // 2. Show skeleton row while request is in-flight
    this._showAddingIndicator(variantId);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_to_cart',
      variant_id: variantId,
      quantity: quantity
    });

    try {
      // 3. One API call to add the item
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const addedItem = await response.json();

      // Klaviyo: Added to Cart event — powers Abandoned Cart flow
      try {
        var _learnq = window._learnq = window._learnq || [];
        _learnq.push(['track', 'Added to Cart', {
          $value:       addedItem.final_price / 100,
          AddedItemProductName: addedItem.product_title,
          AddedItemProductID:   addedItem.product_id,
          AddedItemVariantID:   addedItem.variant_id,
          AddedItemSKU:         addedItem.sku,
          AddedItemCategories:  addedItem.product_type ? [addedItem.product_type] : [],
          AddedItemImageURL:    addedItem.image ? 'https:' + addedItem.image : '',
          AddedItemURL:         window.location.origin + addedItem.url,
          AddedItemPrice:       (addedItem.final_price / 100).toFixed(2),
          AddedItemQuantity:    addedItem.quantity,
          CheckoutURL:          window.location.origin + '/checkout',
          Items: [{
            ProductID:  addedItem.product_id,
            SKU:        addedItem.sku,
            ProductName: addedItem.product_title,
            Quantity:   addedItem.quantity,
            ItemPrice:  (addedItem.final_price / 100).toFixed(2),
            RowTotal:   (addedItem.final_line_price / 100).toFixed(2),
            ProductURL: window.location.origin + addedItem.url,
            ImageURL:   addedItem.image ? 'https:' + addedItem.image : ''
          }]
        }]);
      } catch(e) {}

      // 4. Always re-fetch from server to get accurate totals (discounts, gift thresholds, etc.)
      this._removeAddingIndicator();
      await this.fetchCart();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this._removeAddingIndicator();
      // Re-sync real state on failure
      await this.fetchCart();
      alert('Unable to add item. Please try again.');
    }
  }

  changeItem(key, quantity) {
    if (this.currentCart && Array.isArray(this.currentCart.items)) {
      // Optimistic UI Update: update list instantly in-memory
      const itemIndex = this.currentCart.items.findIndex(item => item.key === key);
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: quantity === 0 ? 'remove_from_cart' : 'update_cart',
        cart_key: key,
        quantity: quantity
      });

      if (itemIndex > -1) {
        const item = this.currentCart.items[itemIndex];
        const unitPrice = item.final_price || item.price; // Use discounted price if available
        const diff = quantity - item.quantity;

        if (quantity <= 0) {
          this.currentCart.items.splice(itemIndex, 1);
        } else {
          item.quantity = quantity;
          item.final_line_price = unitPrice * quantity;
        }

        this.currentCart.item_count += diff;
        this.currentCart.total_price += diff * unitPrice;
        this.currentCart._optimistic = true; // Flag so renderCart skips gift meter during optimistic renders

        // Render instant feedback
        this.renderCart(this.currentCart);
        this.currentCart._optimistic = false;
      }
    }

    return new Promise((resolve, reject) => {
      if (this.changeDebouncers[key]) {
        clearTimeout(this.changeDebouncers[key]);
      }

      // Capture this timeout's own ID so we can detect if it was superseded
      let myTimeoutId;

      myTimeoutId = setTimeout(async () => {
        try {
          if (this.subtotalEl) {
            this.subtotalEl.innerHTML = `<span class="material-symbols-outlined text-sm animate-spin mr-1 align-middle inline-block">sync</span>${this.formatMoney(this.currentCart.total_price)}`;
            this.subtotalEl.classList.add('opacity-60');
          }

          // Combine quantity update and potential free gift auto-removal into ONE request
          let updates = { [key]: quantity };
          
          const giftMeter = document.getElementById('cart-drawer-gift-meter');
          const giftThresholdAttr = giftMeter ? giftMeter.getAttribute('data-gift-threshold') : null;
          const giftVariantIdsAttr = giftMeter ? giftMeter.getAttribute('data-gift-variant-ids') : null;
          
          if (giftThresholdAttr && giftVariantIdsAttr && this.currentCart && Array.isArray(this.currentCart.items)) {
            const giftThresholdCents = parseInt(giftThresholdAttr) * 100;
            const giftVariantIds = giftVariantIdsAttr.split(',').filter(id => id).map(id => parseInt(id));
            const freeGiftItem = this.currentCart.items.find(i => giftVariantIds.includes(i.variant_id));
            if (freeGiftItem) {
              let qualifyingTotal = this.currentCart.total_price - freeGiftItem.final_line_price;
              if (qualifyingTotal < giftThresholdCents) {
                updates[freeGiftItem.key] = 0;
              }
            }
          }

          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates })
          });
          if (!response.ok) throw new Error('Cart update failed with status: ' + response.status);
          const cart = await response.json();
          this.currentCart = cart; // Always sync internal state to server truth

          // ── Key fix: only re-render if no NEWER click fired while we were waiting ──
          // If changeDebouncers[key] still points to our ID → we're the latest → safe to render.
          // If it points to a different ID → user clicked again during the network wait →
          //   skip full re-render (newer debounce will own it) and only patch the subtotal.
          if (this.changeDebouncers[key] === myTimeoutId) {
            this.renderCart(cart);
          } else {
            // Keep totals accurate without a jarring full re-render
            if (this.subtotalEl) {
              this.subtotalEl.innerText = this.formatMoney(cart.total_price);
            }
          }

          // Emit a global event to notify the cart page if open
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
          resolve(cart);
        } catch (error) {
          console.error('Error changing cart item:', error);
          this.fetchCart(); // Revert to real state on network failure
          reject(error);
        } finally {
          // Only clean up our own ID — don't delete a newer debounce that replaced us
          if (this.changeDebouncers[key] === myTimeoutId) {
            delete this.changeDebouncers[key];
          }
        }
      }, 300);

      this.changeDebouncers[key] = myTimeoutId;
    });
  }

  async toggleGiftWrap(checkbox) {
    const variantId = parseInt(checkbox.getAttribute('data-variant-id'));
    if (!variantId) return;

    if (checkbox.checked) {
      await this.addItem(variantId, 1);
    } else {
      if (this.currentCart) {
        const item = this.currentCart.items.find(i => i.variant_id === variantId);
        if (item) {
          await this.changeItem(item.key, 0);
        }
      }
    }
  }

}

// Initialize globally
document.addEventListener('DOMContentLoaded', () => {
  window.CartDrawer = new CartDrawer();
});
